import React from 'react';
import { Field } from 'redux-form';
import { useController, Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Tooltip from '../tooltip';
import Icon from '../icon';
import { combineClassNames } from '@common/utils/combineClassNames';

export type Props = {
  text: any;
  input?: { value: any };
  description?: string;
  forInput?: string;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  className?: string;
};

const FormLabel = ({
  input, text, description, forInput, maxLength, minLength, required, className,
}: Props) => {
  const { t } = useTranslation();
  return (
    <label className={combineClassNames('Form__Label', className)} htmlFor={forInput}>
      {text}
      {required ? <span className="Form__Label__required">*</span> : null}
      {minLength && input && input.value.length < minLength
        ? ` (${t('common:form_label_min_characters', { minLength })})`
        : maxLength &&
          input &&
          ` (${t('common:form_label_max_characters', {
            length: input.value.length,
            maxLength,
          })})`}
      {description && (
        <Tooltip title={description}>
          <Icon type="info" className="tooltip-icon" />
        </Tooltip>
      )}
    </label>
  );
};

// Hook Form version of FormLabel
export interface FormLabelHookFormProps<T extends FieldValues> {
  text: any;
  name: Path<T>;
  control: Control<T>;
  description?: string;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  forInput?: string;
  className?: string;
  tooltip?: string;
  tooltipClassName?: string;
}

export function FormLabelHookForm<T extends FieldValues>({
  text,
  name,
  control,
  description,
  maxLength,
  minLength,
  required,
  forInput,
  className,
  tooltip,
  tooltipClassName,
}: FormLabelHookFormProps<T>) {
  const { t } = useTranslation();
  const { field } = useController({
    name,
    control,
    defaultValue: '' as any,
  });

  const { value } = field;
  const valueLength = typeof value === 'string' ? value.length : 0;

  return (
    <div className="tw-flex tw-flex-row tw-gap-1 tw-items-center">
      <label
        className={combineClassNames('Form__Label', className)}
        htmlFor={forInput || name}
      >
        {text}
        {required ? <span className="Form__Label__required">*</span> : null}
        {minLength != null && valueLength < minLength
          ? ` (${t('common:form_label_min_characters', { minLength })})`
          : maxLength != null &&
            ` (${t('common:form_label_max_characters', {
              length: valueLength,
              maxLength,
            })})`}
        {description && (
          <Tooltip title={description}>
            <Icon type="info" className="tooltip-icon" />
          </Tooltip>
        )}
      </label>
      {tooltip && (
        <Tooltip title={tooltip}>
          <div className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4">
            <Icon
              type="info"
              color="gray"
              customClassName={combineClassNames(
                '!tw-w-4 !tw-h-4',
                tooltipClassName
              )}
            />
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export { FormLabel };

export default (props: Props) => (props.forInput ? (
  <Field name={props.forInput} {...props} component={FormLabel} />
  ) : (
    <FormLabel {...props} />
  ));
